import type { JSONContent } from '@tiptap/core'

import {
  expandCitationIds,
  maskMarkdownCodeSegments,
} from '@/components/chat-v2/lib/citations'

import type { Source } from '@/types/chat-v2'

export const CHAT_CITATION_NODE_NAME = 'chatCitation'

const NUMBERED_CITATION_PATTERN = /\[(\d+)\](?!\()/g
const HTML_CITATION_PATTERN =
  /<(cite|source-citation)\s+([^>]+?)\s*(?:\/>|><\/source-citation>)/g

const normalizeSourceIds = (ids: number[]) => {
  return Array.from(new Set(ids.filter((id) => Number.isFinite(id))))
}

const resolveSourcesForIds = (
  ids: number[],
  sourcesById: ReadonlyMap<number, Source>,
) => {
  const orderedIds = normalizeSourceIds(ids)
  return orderedIds
    .map((id) => sourcesById.get(id))
    .filter((source): source is Source => source != null)
}

export const encodeChatCitationSources = (sources: Source[]): string => {
  try {
    return encodeURIComponent(JSON.stringify(sources))
  } catch {
    return ''
  }
}

export const decodeChatCitationSources = (
  value: string | null | undefined,
): Source[] => {
  if (!value) return []

  try {
    const decoded = decodeURIComponent(value)
    const parsed = JSON.parse(decoded)
    if (!Array.isArray(parsed)) {
      return []
    }
    return parsed.filter((source): source is Source => {
      return (
        source != null &&
        typeof source === 'object' &&
        typeof source.id === 'number' &&
        typeof source.source_type === 'string'
      )
    })
  } catch {
    return []
  }
}

export const buildChatCitationTag = (sources: Source[]): string => {
  return `<chat-citation data-sources="${encodeChatCitationSources(sources)}"></chat-citation>`
}

const areAllSourceIdsResolved = (
  ids: number[],
  sourcesById: ReadonlyMap<number, Source>,
) => {
  return (
    resolveSourcesForIds(ids, sourcesById).length ===
    normalizeSourceIds(ids).length
  )
}

const replaceNumberedCitations = (
  value: string,
  sourcesById: ReadonlyMap<number, Source>,
) => {
  const matches = [...value.matchAll(NUMBERED_CITATION_PATTERN)]
  if (matches.length === 0) {
    return value
  }

  const chunks: string[] = []
  let lastIndex = 0
  let groupedIds: number[] = []
  let groupedRaw = ''

  const flushGroup = () => {
    if (groupedIds.length === 0) return

    const resolvedSources = resolveSourcesForIds(groupedIds, sourcesById)
    chunks.push(
      areAllSourceIdsResolved(groupedIds, sourcesById)
        ? buildChatCitationTag(resolvedSources)
        : groupedRaw,
    )

    groupedIds = []
    groupedRaw = ''
  }

  matches.forEach((match, matchIndex) => {
    const [fullMatch, sourceIdText] = match
    const startIndex = match.index ?? 0
    const endIndex = startIndex + fullMatch.length
    const previousMatch = matchIndex > 0 ? matches[matchIndex - 1] : null
    const previousEndIndex = previousMatch
      ? (previousMatch.index ?? 0) + previousMatch[0].length
      : -1

    const isConsecutive =
      previousMatch !== null && startIndex === previousEndIndex
    const sourceId = Number.parseInt(sourceIdText, 10)

    if (isConsecutive && groupedIds.length > 0) {
      groupedIds.push(sourceId)
      groupedRaw += fullMatch
      lastIndex = endIndex
      return
    }

    flushGroup()

    if (startIndex > lastIndex) {
      chunks.push(value.slice(lastIndex, startIndex))
    }

    groupedIds = [sourceId]
    groupedRaw = fullMatch
    lastIndex = endIndex
  })

  flushGroup()

  if (lastIndex < value.length) {
    chunks.push(value.slice(lastIndex))
  }

  return chunks.join('')
}

const replaceHtmlCitationTags = (
  value: string,
  sourcesById: ReadonlyMap<number, Source>,
) => {
  return value.replace(HTML_CITATION_PATTERN, (match, tagName, attributes) => {
    const idValue =
      tagName === 'source-citation'
        ? (attributes.match(/data-ids=["']([^"']+)["']/)?.[1] ??
          attributes.match(/data-id=["']([^"']+)["']/)?.[1] ??
          '')
        : (attributes.match(/\bid=["']([^"']+)["']/)?.[1] ?? '')

    const sourceIds = expandCitationIds(idValue)
      .map((id) => Number.parseInt(id, 10))
      .filter((id) => Number.isFinite(id))

    if (sourceIds.length === 0) {
      return match
    }

    const resolvedSources = resolveSourcesForIds(sourceIds, sourcesById)
    if (!areAllSourceIdsResolved(sourceIds, sourcesById)) {
      return match
    }

    return buildChatCitationTag(resolvedSources)
  })
}

export const hydrateMarkdownWithChatCitationTags = (
  markdown: string,
  sourcesById: ReadonlyMap<number, Source>,
) => {
  if (!markdown.trim() || sourcesById.size === 0) {
    return markdown
  }

  const { maskedMarkdown, restore } = maskMarkdownCodeSegments(markdown)

  return restore(
    replaceHtmlCitationTags(
      replaceNumberedCitations(maskedMarkdown, sourcesById),
      sourcesById,
    ),
  )
}

const parseJsonContent = (
  content: string | JSONContent | null | undefined,
): JSONContent | null => {
  if (!content) {
    return null
  }

  if (typeof content !== 'string') {
    if ('default' in content && content.default) {
      return content.default as JSONContent
    }
    return content
  }

  try {
    const parsed = JSON.parse(content) as JSONContent
    if ('default' in parsed && parsed.default) {
      return parsed.default as JSONContent
    }
    return parsed
  } catch {
    return null
  }
}

const visitContent = (
  node: JSONContent,
  visitor: (currentNode: JSONContent) => void,
) => {
  visitor(node)

  for (const child of node.content ?? []) {
    visitContent(child, visitor)
  }
}

export const collectChatCitationSourcesFromContent = (
  content: string | JSONContent | null | undefined,
) => {
  const parsedContent = parseJsonContent(content)
  if (!parsedContent) {
    return []
  }

  const sourcesById = new Map<number, Source>()

  visitContent(parsedContent, (node) => {
    if (node.type !== CHAT_CITATION_NODE_NAME) {
      return
    }

    const sources = Array.isArray(node.attrs?.sources)
      ? (node.attrs?.sources as Source[])
      : []

    for (const source of sources) {
      if (
        source != null &&
        typeof source.id === 'number' &&
        !sourcesById.has(source.id)
      ) {
        sourcesById.set(source.id, source)
      }
    }
  })

  return Array.from(sourcesById.values())
}
